import type { ReactNode } from 'react';
import { Badge, Card, cx } from '@/components/ui';
import { changeColorClass, formatKrw, formatPercent, formatSignedNumber } from '@/lib/format';
import { HelpButton } from './HelpButton';
import { PROFIT_HELP } from './profitHelp';
import {
  LOW_CASH_RATIO_PERCENT,
  isLowCash,
  priorRoundsProfit,
  winRateText,
  type AccountStats,
} from './accountStats';

/**
 * 모의투자 계좌 카드. 추천 3계좌(PaperPage)와 저점매수 3계좌(BandPanel - 저점매수 · 리스크 · 챌린저)가 같이 쓴다.
 * (처음 적을 때는 저점매수가 2계좌였다 · 9/28 I-78 2차)
 * (2026-09-12 사용자 요청)
 *
 * 🔴 왜 하나로 합쳤나
 *   두 곳이 각자 Stat 을 갖고 "총 평가액·손익·수익률·예수금" 과 "총자산·수익률·예수금·시드"
 *   로 조금씩 다르게 그리고 있었다. 같은 뜻의 숫자가 탭마다 다른 자리·다른 이름으로
 *   나오면 여섯 계좌를 오가며 읽을 때 매번 눈이 다시 찾는다. (CLAUDE.md 5.2절)
 *
 * 세 줄로 답한다.
 *   1행  지금 얼마인가          총자산 · 누적 수익률(밑에 이번 회차) · 예수금(비중) · 최대 낙폭
 *   2행  그 돈이 어떻게 이뤄졌나  원금 + 지난 회차까지 + 실현 + 평가 = 총자산
 *   3행  다 팔면 얼마 남나       지금 다 팔면(수수료·세금 뺀 값) · 승률(모수) · 미실현
 *
 * 🔴 누적 크게 · 회차 작게 (2026-10-19 사용자 피드백 · 사용자가 고른 표시)
 *   4회차로 넘어가며 수익률 분모가 회차 시작 평가액이 되자 3회차까지의 손익이 카드에서 사라져 보였다.
 *   큰 숫자는 처음 원금 대비 누적, 그 밑 작은 줄이 이번 회차다. 원금과 회차 시작이 같으면 작은 줄은 뺀다(같은 숫자 두 번)
 *
 * 🔴 승률과 미실현은 반드시 나란히 둔다 (사용자 지시)
 *   저점매수 계좌는 오른 것을 바로 팔고 안 오른 것을 들고 있는 규칙이라
 *   승률 80% 옆에 미실현 −53만이 붙는 모양이 정상이다. 승률만 읽히면
 *   "잘 되고 있다" 로 오해한다. 한 span 에 nowrap 으로 묶어 폰에서도 안 갈라진다.
 *
 * 🔴 비율은 백엔드 값을 그대로 찍는다. 여기서 나누지 않는다 (CLAUDE.md 6.1절)
 *   차이(시드 대비·손익)만 원 단위 정수 뺄셈이라 프론트에서 한다.
 *
 * @param children 카드 아래 각주. 호출부마다 다르다 (시작일·보유 수·장중/종가 기준 등)
 */
export function AccountStatsCard({
  title,
  stats,
  children,
}: {
  title: ReactNode;
  stats: AccountStats;
  children?: ReactNode;
}) {
  // 지난 회차까지 쌓인 손익 = 회차 시작 금액(seed 필드) - 원금. 원금으로 이번 회차를 시작했으면 null (accountStats.priorRoundsProfit)
  const prior = priorRoundsProfit(stats);
  // 지금 다 팔면 원금 대비 얼마가 남나. 누적 손익보다 매도 수수료·거래세만큼 작다
  const afterSell = stats.liquidationValue - stats.principal;
  const lowCash = isLowCash(stats.cashRatioPercent);

  return (
    <Card title={title}>
      {/* ── 1행: 지금 얼마인가 ── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="총자산" value={formatKrw(stats.total)} />
        {/* 큰 숫자 = 원금 대비 누적 · 작은 줄 = 이번 회차(회차 시작 금액 대비). 비율은 둘 다 백엔드 값이다 */}
        <Stat
          label="누적 수익률"
          value={formatPercent(stats.cumulativeReturnPercent)}
          className={changeColorClass(stats.cumulativeReturnPercent)}
          sub={
            <>
              <span className={changeColorClass(stats.cumulativeProfit)}>
                {formatSignedNumber(stats.cumulativeProfit)}원
              </span>
              {prior !== null && (
                <span className="block whitespace-nowrap">
                  이번 회차{' '}
                  <span className={changeColorClass(stats.returnPercent)}>
                    {formatPercent(stats.returnPercent)}
                  </span>
                </span>
              )}
            </>
          }
        />
        <Stat
          label="예수금"
          value={formatKrw(stats.cash)}
          /*
            예수금 비중이 낮으면 배지를 붙인다. 문턱과 이유는 accountStats.LOW_CASH_RATIO_PERCENT.
            🔴 경고가 아니라 정보다 - 사용자는 실제 현금을 따로 갖고 있다. 그래서 warning 이다
          */
          badge={
            lowCash && <Badge tone="warning">예수금 {LOW_CASH_RATIO_PERCENT}% 미만</Badge>
          }
          sub={
            stats.cashRatioPercent === null
              ? '비중 —'
              : `비중 ${stats.cashRatioPercent.toFixed(1)}%`
          }
        />
        {/* 🔴 시드 대비가 아니라 고점 대비다. 실전에서 견뎌야 하는 낙폭이 이것이다 (비교 표와 같은 뜻)
            ✏️ 2026-10-19 — 이 값 · 아래 승률은 이번 회차 기록으로 센다. 큰 숫자(누적)와 기간이 다른 계좌(지난 회차가 있는 계좌)에서만 "이번 회차" 를 밝힌다 */}
        <Stat
          label={prior !== null ? '최대 낙폭(이번 회차)' : '최대 낙폭'}
          value={stats.maxDrawdownPercent === null ? '—' : formatPercent(stats.maxDrawdownPercent)}
          className={changeColorClass(stats.maxDrawdownPercent)}
        />
      </div>

      {/* ── 2행: 총자산이 어떻게 이뤄졌나 ── */}
      <div className="mt-4">
        <p className="flex items-center text-xs text-fg-subtle">
          총자산이 어떻게 이뤄졌나
          {/* 실현·평가·손익 세 말의 설명은 한 곳(profitHelp.ts)에서 온다. 표 머리의 ? 와 같은 글이다 */}
          <HelpButton help={PROFIT_HELP} />
        </p>
        {/*
          🔴 폰(375px)에서는 네 항이 한 줄에 안 들어간다
            카드 안쪽 폭이 약 311px 인데 항 하나가 라벨·금액을 세로로 쌓아도 110px 안팎이라
            두 줄로 접힌다. 그때 "= 총자산" 의 등호만 앞줄 끝에 남고 값이 다음 줄로
            가면 식이 안 읽힌다. 그래서 연산자와 그 다음 항을 한 flex 아이템으로 묶어
            줄바꿈이 [원금] [+ 지난 회차까지] [+ 실현] [+ 평가] [= 총자산] 사이에서만 일어나게 한다.
            어느 줄로 접혀도 "= 총자산 20,867,200원" 이 한 덩어리로 남는다.
        */}
        <div className="mt-1 flex flex-wrap items-stretch gap-x-1.5 gap-y-1.5">
          {/*
            🔴 첫 항은 원금이다 (2026-10-19 사용자 피드백). 원금 + 지난 회차까지 = 이번 회차 시작 금액(seed 필드 · U-31)이라
              실현 · 평가(이번 회차 몫)를 더하면 그대로 총자산이다. 지난 회차까지 항은 0 이 아닐 때만 그린다
          */}
          <Term label="원금" value={formatKrw(stats.principal)} />
          {prior !== null && (
            <Term
              op="+"
              label="지난 회차까지"
              value={`${formatSignedNumber(prior)}원`}
              className={changeColorClass(prior)}
            />
          )}
          <Term
            op="+"
            label="실현 손익"
            value={`${formatSignedNumber(stats.realizedPnl)}원`}
            className={changeColorClass(stats.realizedPnl)}
          />
          <Term
            op="+"
            label="평가 손익"
            value={`${formatSignedNumber(stats.unrealizedPnl)}원`}
            className={changeColorClass(stats.unrealizedPnl)}
          />
          <Term op="=" label="총자산" value={formatKrw(stats.total)} />
        </div>
      </div>

      {/* ── 3행: 다 팔면 얼마 남나 ── */}
      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        {/* 금액 조각마다 tabular(nowrap)라 숫자는 안 쪼개지고, 조각 사이에서만 접힌다 */}
        <span>
          지금 다 팔면{' '}
          <span className="font-semibold tabular text-fg">{formatKrw(stats.liquidationValue)}</span>{' '}
          <span className={cx('tabular', changeColorClass(afterSell))}>
            (원금 대비 {formatSignedNumber(afterSell)}원)
          </span>
        </span>
        {/* 🔴 승률과 미실현은 한 덩어리다. 위 컴포넌트 주석 참조 */}
        <span className="whitespace-nowrap text-fg-muted">
          {prior !== null && '이번 회차 '}
          {winRateText(stats)} · 미실현{' '}
          <span className={cx('tabular', changeColorClass(stats.unrealizedPnl))}>
            {formatSignedNumber(stats.unrealizedPnl)}원
          </span>
        </span>
      </div>
      <p className="mt-1 text-xs text-fg-subtle">
        "지금 다 팔면" 은 매도 수수료·거래세를 뺀 값입니다. 미실현은 아직 안 판 종목의
        평가 손익이라 승률에 들어 있지 않습니다.
      </p>

      {children}
    </Card>
  );
}

/**
 * 1행의 숫자 한 칸. 라벨 옆에 배지, 값 아래에 작은 보조 글을 붙일 수 있다.
 */
function Stat({
  label,
  value,
  className,
  sub,
  badge,
}: {
  label: string;
  value: string;
  className?: string;
  /** 값 아래 작은 글씨. 예수금 비중, 손익 금액 */
  sub?: ReactNode;
  /** 라벨 옆 배지. 폰 2열에서 넘치면 flex-wrap 으로 라벨 아래로 내려간다 */
  badge?: ReactNode;
}) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 text-xs text-fg-subtle">
        <span>{label}</span>
        {badge}
      </div>
      <p className={cx('mt-0.5 text-lg tabular', className)}>{value}</p>
      {sub && <p className="text-xs tabular text-fg-subtle">{sub}</p>}
    </div>
  );
}

/**
 * 2행 수식의 항 하나. 라벨 위·값 아래의 작은 카드다.
 *
 * 한 줄 문장("시드 20,000,000 + 실현 +1,402,441 ...")으로 두면 폰에서 아무 데서나
 * 접혀 어느 숫자가 무엇인지 잃는다. 항마다 라벨을 붙여 두면 접힌 뒤에도 읽힌다.
 *
 * @param op 이 항 앞의 연산자. 항과 함께 한 flex 아이템이라 줄바꿈이 둘 사이를 가르지 않는다
 */
function Term({
  op,
  label,
  value,
  className,
}: {
  op?: '+' | '=';
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {op && <span className="text-sm text-fg-subtle">{op}</span>}
      <div className="rounded-md border border-line px-2 py-1">
        <p className="text-[11px] leading-4 text-fg-subtle">{label}</p>
        <p className={cx('text-sm tabular', className)}>{value}</p>
      </div>
    </div>
  );
}
